import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react';

// Set REACT_APP_BACKEND_URL in your S3/CloudFront build environment
// e.g. https://xxxxxxxxxx.execute-api.af-south-1.amazonaws.com
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    shipmentType: '',
    mode: '',
    route: '',
    message: ''
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you! Your quote request has been received. We will respond within 24 hours.'
        });
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          shipmentType: '',
          mode: '',
          route: '',
          message: ''
        });
      } else {
        setStatus({
          type: 'error',
          message: data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Unable to submit form. Please email us directly at admin@netexecglobal.co.za'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Get in Touch</span>
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase mt-4 mb-6"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Request a Quote
            </h1>
            <p className="text-xl leading-relaxed text-slate-600">
              Get a customized freight forwarding and customs clearance quote for your import or export shipment. Our team responds within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 
                  className="text-2xl md:text-4xl font-bold tracking-tight uppercase mb-6"
                  style={{fontFamily: 'Barlow Condensed, sans-serif'}}
                >
                  Contact Information
                </h2>
                <p className="text-base text-slate-600 mb-8">
                  Reach out directly or use the quote form. We're here to help with your freight and customs needs.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                    <a 
                      href="tel:+27119801717" 
                      className="text-slate-600 hover:text-orange-500 transition-colors block"
                    >
                      +27 11 980 1717
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">WhatsApp</h3>
                    <a 
                      href="https://wa.me/27763296351" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-orange-500 transition-colors block"
                    >
                      +27 76 329 6351
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a 
                      href="mailto:admin@netexecglobal.co.za" 
                      className="text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      admin@netexecglobal.co.za
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Location</h3>
                    <p className="text-slate-600">South Africa</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
                <h3 className="font-semibold text-slate-900 mb-2">Response Time</h3>
                <p className="text-sm text-slate-600">
                  We respond to all quote requests within 24 business hours with detailed pricing and timeline information.
                </p>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="form-name"
                      className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-900 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="form-company"
                      className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                      placeholder="ABC Trading"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="form-email"
                      className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-900 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="form-phone"
                      className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                      placeholder="+27 12 345 6789"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="shipmentType" className="block text-sm font-medium text-slate-900 mb-2">
                      Shipment Type *
                    </label>
                    <select
                      id="shipmentType"
                      name="shipmentType"
                      required
                      value={formData.shipmentType}
                      onChange={handleChange}
                      data-testid="form-shipment-type"
                      className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                    >
                      <option value="">Select...</option>
                      <option value="import">Import</option>
                      <option value="export">Export</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mode" className="block text-sm font-medium text-slate-900 mb-2">
                      Preferred Mode *
                    </label>
                    <select
                      id="mode"
                      name="mode"
                      required
                      value={formData.mode}
                      onChange={handleChange}
                      data-testid="form-mode"
                      className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                    >
                      <option value="">Select...</option>
                      <option value="air">Air Freight</option>
                      <option value="ocean">Ocean Freight</option>
                      <option value="not-sure">Not Sure</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="route" className="block text-sm font-medium text-slate-900 mb-2">
                    Route / Origin & Destination *
                  </label>
                  <input
                    type="text"
                    id="route"
                    name="route"
                    required
                    value={formData.route}
                    onChange={handleChange}
                    data-testid="form-route"
                    className="w-full border-b-2 border-slate-200 focus:border-orange-500 bg-transparent px-0 py-3 outline-none transition-colors text-slate-900"
                    placeholder="e.g., Paris to Johannesburg"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-900 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="form-message"
                    className="w-full border-2 border-slate-200 focus:border-orange-500 bg-transparent px-4 py-3 rounded-sm outline-none transition-colors text-slate-900 resize-none"
                    placeholder="Tell us about your cargo (type, weight, dimensions, timeline, any special requirements...)"
                  ></textarea>
                </div>

                {status.message && (
                  <div
                    className={`flex items-start gap-3 p-4 rounded-sm ${
                      status.type === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}
                    data-testid="form-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <p
                      className={`text-sm ${
                        status.type === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}
                    >
                      {status.message}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="form-submit-btn"
                  className="bg-slate-900 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-500 transition-colors duration-300 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
